/**
 * capital_works.ts — the capital's WORKS as dressing geometry (capital wave
 * phase 3, 2026-09-15): the megawall (curtain bays, buttresses, an outer
 * parapet over a walkable deck), twelve gate openings with twin towers, a
 * lintel carrying the deck across each gate, a square-spiral stair in every
 * tower, and the civic plaza (paving, inlay rings, the offset fountain, the
 * colonnade, banner poles, benches, the notice board, stage sockets).
 *
 * Layout comes from capital_wall.ts (pure); this file only instances it
 * through the dressing builder's own Parts / mat4 / plank (passed in — no
 * import cycle) and records colliders (≤ 16 m pieces), hover solids (one per
 * bay / tower) and NPC keep-clears (gate throats, fountain, stage, civic
 * parcels).
 */
import * as THREE from 'three/webgpu';
import type { SettlementSite } from '../../../shared/settlements.js';
import { capitalTheta0, CAPITAL_NAMES, type Civilization } from '../../../shared/capitals.js';
import {
  capitalWallLayout, capitalPlazaLayout, colliderPieces, tangentYaw,
  WALL_T, TOWER_W, PARAPET_H, PARAPET_T, BUTTRESS_W, BUTTRESS_OUT, LINTEL_H,
  STEP_TREAD, STEP_W, DOOR_W, DOOR_H, DECK_DOOR_H, DECK_W, FOUNTAIN_R, COLUMN_H,
} from './capital_wall.js';
import type { DressingCollider, DressingSolid, DressingSeat, MatSet } from './settlement_dressing.js';

export interface CapitalWorksInput {
  site: SettlementSite; seed: string; civ: Civilization; M: MatSet; group: THREE.Group;
  parts: { add(kind: string, geo: () => THREE.BufferGeometry, mat: THREE.Material, m4: THREE.Matrix4): void };
  mat4: (x: number, y: number, z: number, yaw: number, sx: number, sy: number, sz: number) => THREE.Matrix4;
  plank: (text: string, civ: Civilization, wM: number, hM: number) => THREE.Mesh;
  gy: (wx: number, wz: number) => number;
  colliders: DressingCollider[]; solids: DressingSolid[]; seats: DressingSeat[];
}

export interface CapitalWorksStats { bays: number; towers: number; steps: number; colliders: number; plazaParts: number }

/** The dressing yaw of a length running along (dx, dz). */
function yawAlong(dx: number, dz: number): number { return Math.atan2(dx, dz) + Math.PI / 2; }

const SHELL_T = 1; // tower shell thickness

export function buildCapitalWorks(inp: CapitalWorksInput): CapitalWorksStats {
  const { site, civ, M, group, parts, mat4, plank, gy, colliders, solids, seats } = inp;
  const th = capitalTheta0(site.siteSeed);
  const W = capitalWallLayout(th, civ);
  const wallMat = civ === 'a' ? M.wallA : M.wallB;
  const clothMat = civ === 'a' ? M.clothA : M.clothB;
  const box = (): THREE.BufferGeometry => new THREE.BoxGeometry(1, 1, 1);
  const cyl = (n: number) => (): THREE.BufferGeometry => new THREE.CylinderGeometry(1, 1, 1, n);
  const c0 = colliders.length;
  let steps = 0;

  // ── The curtain: one bay at a time ──
  for (const b of W.bays) {
    const wx = site.centerX + b.x, wz = site.centerZ + b.z;
    const y = gy(wx, wz);
    const tx = -Math.sin(b.ang), tz = Math.cos(b.ang);   // tangent
    const rx = Math.cos(b.ang), rz = Math.sin(b.ang);    // radial (outward)
    parts.add('cwall', box, wallMat, mat4(wx, y + W.h / 2, wz, b.yaw, b.len + 0.4, W.h, WALL_T));
    // Outer parapet over the deck (the deck = the wall's top inside it, DECK_W wide).
    const po = WALL_T / 2 - PARAPET_T / 2;
    parts.add('cparapet', box, M.stoneDark, mat4(wx + rx * po, y + W.h + PARAPET_H / 2, wz + rz * po, b.yaw, b.len + 0.4, PARAPET_H, PARAPET_T));
    // Buttress bay at the bay's start joint.
    const a0 = b.ang - (b.len / 2) / W.r;
    const bx = site.centerX + Math.cos(a0) * W.r, bz = site.centerZ + Math.sin(a0) * W.r;
    parts.add('cbuttress', box, M.stoneDark, mat4(bx, y + (W.h + 2) / 2, bz, tangentYaw(a0), BUTTRESS_W, W.h + 2, WALL_T + 2 * BUTTRESS_OUT));
    for (const [off, len] of colliderPieces(b.len + 0.4)) {
      const cx = wx + tx * off, cz = wz + tz * off;
      colliders.push({ hx: len / 2, hy: W.h / 2, hz: WALL_T / 2, x: cx, y: y + W.h / 2, z: cz, yaw: b.yaw });
      colliders.push({ hx: len / 2, hy: PARAPET_H / 2, hz: PARAPET_T / 2, x: cx + rx * po, y: y + W.h + PARAPET_H / 2, z: cz + rz * po, yaw: b.yaw });
    }
    solids.push({ x: wx, z: wz, hx: b.len / 2 + 0.2, hz: WALL_T / 2, yaw: b.yaw, base: y - 1, top: y + W.h });
  }
  void DECK_W;

  // ── Gate towers: a hollow shell (four faces), a cap, the door, the deck doors, the stair ──
  W.towers.forEach((t, ti) => {
    const wx = site.centerX + t.x, wz = site.centerZ + t.z;
    const y = gy(wx, wz);
    const tx = -Math.sin(t.ang), tz = Math.cos(t.ang);
    const rx = Math.cos(t.ang), rz = Math.sin(t.ang);
    const yawT = t.yaw;                 // length along the tangent
    const yawR = yawAlong(rx, rz);      // length along the radial
    const half = TOWER_W / 2 - SHELL_T / 2; // face centre offset from the tower centre
    parts.add('ctower', box, wallMat, mat4(wx, y + W.towerH / 2, wz, yawT, TOWER_W, W.towerH, TOWER_W));
    parts.add('ctowercap', box, M.stoneDark, mat4(wx, y + W.towerH + 0.75, wz, yawT, TOWER_W + 2, 1.5, TOWER_W + 2));
    // Outer face (+r): one slab.
    colliders.push({ hx: TOWER_W / 2, hy: W.towerH / 2, hz: SHELL_T / 2, x: wx + rx * half, y: y + W.towerH / 2, z: wz + rz * half, yaw: yawT });
    // Inner face (−r): the DOOR — two jambs + the piece above.
    {
      const fx = wx - rx * half, fz = wz - rz * half;
      const jamb = (TOWER_W - DOOR_W) / 2;
      for (const s of [-1, 1]) colliders.push({ hx: jamb / 2, hy: W.towerH / 2, hz: SHELL_T / 2, x: fx + tx * s * (DOOR_W / 2 + jamb / 2), y: y + W.towerH / 2, z: fz + tz * s * (DOOR_W / 2 + jamb / 2), yaw: yawT });
      colliders.push({ hx: DOOR_W / 2, hy: (W.towerH - DOOR_H) / 2, hz: SHELL_T / 2, x: fx, y: y + DOOR_H + (W.towerH - DOOR_H) / 2, z: fz, yaw: yawT });
    }
    // The faces along the wall (±t): the DECK DOOR at wall height, centred on the wall's line.
    for (const s of [-1, 1]) {
      const fx = wx + tx * s * half, fz = wz + tz * s * half;
      colliders.push({ hx: TOWER_W / 2, hy: W.h / 2, hz: SHELL_T / 2, x: fx, y: y + W.h / 2, z: fz, yaw: yawR });
      const above = W.towerH - W.h - DECK_DOOR_H;
      colliders.push({ hx: TOWER_W / 2, hy: above / 2, hz: SHELL_T / 2, x: fx, y: y + W.h + DECK_DOOR_H + above / 2, z: fz, yaw: yawR });
      const side = (TOWER_W - DECK_W) / 2;
      for (const q of [-1, 1]) colliders.push({ hx: side / 2, hy: DECK_DOOR_H / 2, hz: SHELL_T / 2, x: fx + rx * q * (DECK_W / 2 + side / 2), y: y + W.h + DECK_DOOR_H / 2, z: fz + rz * q * (DECK_W / 2 + side / 2), yaw: yawR });
    }
    // Roof.
    colliders.push({ hx: TOWER_W / 2, hy: 0.5, hz: TOWER_W / 2, x: wx, y: y + W.towerH - 0.5, z: wz, yaw: yawT });
    solids.push({ x: wx, z: wz, hx: TOWER_W / 2, hz: TOWER_W / 2, yaw: yawT, base: y - 1, top: y + W.towerH });
    // The stair: solid blocks from the base to each tread's top.
    for (const st of W.stairs[ti]) {
      const sx = site.centerX + st.x, sz = site.centerZ + st.z;
      parts.add('cstep', box, M.stone, mat4(sx, y + st.top / 2, sz, st.yaw, STEP_TREAD, st.top, STEP_W));
      colliders.push({ hx: STEP_TREAD / 2, hy: st.top / 2, hz: STEP_W / 2, x: sx, y: y + st.top / 2, z: sz, yaw: st.yaw });
      steps++;
    }
  });

  // ── Lintels: the deck runs level across every gate; a civ banner hangs beneath. ──
  for (const l of W.lintels) {
    const wx = site.centerX + l.x, wz = site.centerZ + l.z;
    const y = gy(wx, wz);
    parts.add('clintel', box, wallMat, mat4(wx, y + W.h - LINTEL_H / 2, wz, l.yaw, l.len, LINTEL_H, WALL_T));
    colliders.push({ hx: l.len / 2, hy: LINTEL_H / 2, hz: WALL_T / 2, x: wx, y: y + W.h - LINTEL_H / 2, z: wz, yaw: l.yaw });
    const banner = new THREE.Mesh(new THREE.PlaneGeometry(4, 8), clothMat);
    banner.position.set(wx, y + W.h - LINTEL_H - 4.5, wz);
    banner.rotation.y = l.ang;
    group.add(banner);
    seats.push({ x: wx, z: wz, r: 16 }); // the gate throat stays open
  }

  // ── The plaza ──
  const P = capitalPlazaLayout(th);
  const y0 = gy(site.centerX, site.centerZ);
  const ux = Math.cos(th), uz = Math.sin(th);
  const yawU = yawAlong(ux, uz);
  const at = (p: { x: number; z: number }): [number, number] => [site.centerX + p.x, site.centerZ + p.z];
  let plazaParts = 0;
  const [pcx, pcz] = at({ x: P.cx, z: P.cz });
  parts.add('cpave', box, M.stone, mat4(pcx, y0 + 0.06, pcz, yawU, 2 * P.hw, 0.12, 2 * P.hd)); plazaParts++;
  const flatRing = (r0: number, r1: number) => (): THREE.BufferGeometry => { const g = new THREE.RingGeometry(r0, r1, 72); g.rotateX(-Math.PI / 2); return g; };
  [[30, 32], [55, 57], [80, 82]].forEach(([r0, r1], i) => { parts.add(`cring${i}`, flatRing(r0, r1), M.stoneDark, mat4(pcx, y0 + 0.13, pcz, 0, 1, 1, 1)); plazaParts++; });
  // The fountain: three tiers, a water sheet, a spire; a basin collider + a hover solid.
  const [fx, fz] = at(P.fountain);
  const tiers: Array<[number, number, number]> = [[FOUNTAIN_R[0], 1.2, 0], [FOUNTAIN_R[1], 1.0, 1.2], [FOUNTAIN_R[2], 0.9, 2.2]];
  tiers.forEach(([r, h, base], i) => { parts.add(`cfountain${i}`, cyl(28), M.stoneDark, mat4(fx, y0 + base + h / 2, fz, 0, r, h, r)); plazaParts++; });
  parts.add('cwater', cyl(28), M.water, mat4(fx, y0 + 1.2 - 0.15, fz, 0, FOUNTAIN_R[0] - 0.6, 0.3, FOUNTAIN_R[0] - 0.6)); plazaParts++;
  parts.add('cspire', cyl(10), M.stoneDark, mat4(fx, y0 + 3.1 + 2.5, fz, 0, 0.6, 5, 0.6)); plazaParts++;
  colliders.push({ hx: FOUNTAIN_R[0], hy: 0.7, hz: FOUNTAIN_R[0], x: fx, y: y0 + 0.7, z: fz, yaw: 0 });
  solids.push({ x: fx, z: fz, hx: FOUNTAIN_R[0], hz: FOUNTAIN_R[0], yaw: 0, base: y0 - 1, top: y0 + 3.5 });
  // The colonnade: columns under a beam along each long edge.
  for (const c of P.columns) {
    const [cx, cz] = at(c);
    parts.add('ccolumn', cyl(12), M.stone, mat4(cx, y0 + COLUMN_H / 2, cz, 0, 0.9, COLUMN_H, 0.9)); plazaParts++;
    colliders.push({ hx: 0.9, hy: COLUMN_H / 2, hz: 0.9, x: cx, y: y0 + COLUMN_H / 2, z: cz, yaw: 0 });
  }
  for (const bm of P.beams) {
    const [bx, bz] = at(bm);
    parts.add('cbeam', box, M.stoneDark, mat4(bx, y0 + COLUMN_H + 0.6, bz, bm.yaw, bm.len, 1.2, 1.6)); plazaParts++;
    const dx = Math.cos(bm.yaw - Math.PI / 2), dz = -Math.sin(bm.yaw - Math.PI / 2); // unused precision helper
    void dx; void dz;
    for (const [off, len] of colliderPieces(bm.len)) {
      colliders.push({ hx: len / 2, hy: 0.6, hz: 0.8, x: bx + ux * off, y: y0 + COLUMN_H + 0.6, z: bz + uz * off, yaw: bm.yaw });
    }
  }
  // Banner poles with civ cloth.
  for (const p of P.poles) {
    const [px, pz] = at(p);
    parts.add('cpole', cyl(8), M.woodDark, mat4(px, y0 + 4.5, pz, 0, 0.16, 9, 0.16)); plazaParts++;
    const cloth = new THREE.Mesh(new THREE.PlaneGeometry(1.6, 5), clothMat);
    cloth.position.set(px + ux * 0.9, y0 + 6.3, pz + uz * 0.9);
    cloth.rotation.y = -th;
    group.add(cloth);
    colliders.push({ hx: 0.2, hy: 4.5, hz: 0.2, x: px, y: y0 + 4.5, z: pz, yaw: 0 });
  }
  for (const b of P.benches) {
    const [bx, bz] = at(b);
    parts.add('cbench', box, M.wood, mat4(bx, y0 + 0.3, bz, b.yaw, 2.2, 0.5, 0.6)); plazaParts++;
    colliders.push({ hx: 1.1, hy: 0.3, hz: 0.3, x: bx, y: y0 + 0.3, z: bz, yaw: b.yaw });
  }
  // The notice board: the capital's name, facing the fountain.
  {
    const [bx, bz] = at(P.board);
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.14, 3, 8), M.woodDark);
    post.position.set(bx, y0 + 1.5, bz);
    group.add(post);
    const pl = plank(CAPITAL_NAMES[civ].toUpperCase(), civ, 5.2, 1.1);
    pl.position.set(bx, y0 + 2.6, bz);
    pl.rotation.y = th + Math.PI / 2;
    group.add(pl);
    seats.push({ x: bx, z: bz, r: 1 });
    plazaParts += 2;
  }
  for (const s of P.stage) { const [sx, sz] = at(s); parts.add('cstage', cyl(16), M.stoneDark, mat4(sx, y0 + 0.2, sz, 0, 3, 0.15, 3)); plazaParts++; }
  for (const c of P.clears) { const [cx, cz] = at(c); seats.push({ x: cx, z: cz, r: c.r }); }

  return { bays: W.bays.length, towers: W.towers.length, steps, colliders: colliders.length - c0, plazaParts };
}
